/**
 * Domain hooks for ARO ID.me step-up content, sourced from the CMS `ui_identity`
 * payload (via the reusable `useGetUIContent` utility) under the global
 * `aro_id_me` object, with automatic fallback to bundled `content.json` when the
 * CMS is unavailable or a field is not published.
 *
 * `useAroIdMeContent` resolves the "Verify your identity" modal's disclaimer
 * copy (`aro_id_me.disclaimer`, fallback `verifyIdentity`). Its `body` field is
 * a Markdown string containing inline links — rendered by the caller with
 * `<Markdown theme={Themes.VEGA}>`.
 *
 * `useAroIdMeResultContent` resolves the step-up verification result screen's
 * copy (`aro_id_me.success` / `aro_id_me.error`, fallback `aroIdMeVerification`).
 */

import type { IconName } from '@mgmresorts/mgm-ui';
import { useMemo } from 'react';

import content from '../../content.json';

import { UI_CONTENT_IDS, useGetUIContent } from './useGetUIContent';

/** CMS `aro_id_me.disclaimer` sub-object (snake_case, all optional). */
export interface AroIdMeDisclaimer {
  title?: string;
  subtitle?: string;
  /** Markdown string with inline links. */
  body?: string;
  primary_cta_label?: string;
  secondary_cta_label?: string;
}

/** CMS `aro_id_me.success` / `aro_id_me.error` result-screen sub-object. */
export interface AroIdMeResult {
  title?: string;
  body?: string;
  cta_label?: string;
  icon?: string;
}

/** CMS `aro_id_me` object published under `ui_identity`. */
export interface AroIdMeEntry {
  disclaimer?: AroIdMeDisclaimer;
  success?: AroIdMeResult;
  error?: AroIdMeResult;
}

/** Root shape of the `ui_identity` CMS payload (aro_id_me slice). */
export interface UIIdentityAroContent {
  aro_id_me?: AroIdMeEntry;
}

/** Resolved disclaimer content returned by `useAroIdMeContent`. */
export interface AroIdMeContent {
  title: string;
  subtitle: string;
  /** Markdown body (with inline links) for `<Markdown>`. */
  body: string;
  primaryCtaLabel: string;
  secondaryCtaLabel: string;
  /** True while the CMS request is in flight. */
  loading: boolean;
  /** Source of the resolved content — useful for diagnostics/telemetry. */
  source: 'cms' | 'fallback';
}

/** Options accepted by `useAroIdMeContent`. */
export interface UseAroIdMeContentOptions {
  /** Skip the CMS fetch (e.g. while the modal is closed). */
  skip?: boolean;
}

/** Which ARO ID.me result screen content to resolve. */
export type AroIdMeResultKind = 'success' | 'error';

/** Resolved success/error result-screen content returned by `useAroIdMeResultContent`. */
export interface AroIdMeResultContent {
  title: string;
  body: string;
  ctaLabel: string;
  icon: IconName;
  /** True while the CMS request is in flight. */
  loading: boolean;
  /** Source of the resolved content — useful for diagnostics/telemetry. */
  source: 'cms' | 'fallback';
}

/** Options accepted by `useAroIdMeResultContent`. */
export interface UseAroIdMeResultContentOptions {
  /** Skip the CMS fetch. */
  skip?: boolean;
}

/**
 * `content.json` has a rich literal type from `resolveJsonModule`; access the
 * relevant sub-tree via an `unknown` cast to keep lookups type-safe.
 */
const contentUnsafe = content as unknown as {
  verifyIdentity?: {
    title?: string;
    subtitle?: string;
    body?: string;
    primaryCtaLabel?: string;
    secondaryCtaLabel?: string;
  };
  aroIdMeVerification?: {
    success?: { title?: string; description?: string; cta?: string };
    failure?: { title?: string; description?: string; cta?: string };
  };
};

const asString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

/**
 * `<Icon name>` only accepts a fixed union of names, and the CMS payload is
 * free-form text — validate against a local allow-list before it ever reaches
 * the component so a bad/unpublished CMS value can't produce an invalid Icon.
 */
const ARO_RESULT_ICON_DEFAULTS: Record<AroIdMeResultKind, IconName> = {
  success: 'checkmark-circle',
  error: 'exclamation-point-octagon',
};

const ARO_RESULT_VALID_ICONS: readonly IconName[] = ['checkmark-circle', 'exclamation-point-octagon'];

const asIconName = (value: unknown): IconName | undefined =>
  typeof value === 'string' && (ARO_RESULT_VALID_ICONS as readonly string[]).includes(value)
    ? (value as IconName)
    : undefined;

/**
 * Returns the resolved ARO ID.me modal disclaimer copy, sourced from CMS with
 * fallback to bundled defaults. All fields degrade independently.
 */
export function useAroIdMeContent(options: UseAroIdMeContentOptions = {}): AroIdMeContent {
  const { skip = false } = options;
  const { data, loading } = useGetUIContent<UIIdentityAroContent>(UI_CONTENT_IDS.IDENTITY, { skip });

  return useMemo<AroIdMeContent>(() => {
    const disclaimer = data?.getUIContentById?.aro_id_me?.disclaimer;
    const fallback = contentUnsafe.verifyIdentity ?? {};

    const cmsTitle = asString(disclaimer?.title);
    const cmsSubtitle = asString(disclaimer?.subtitle);
    const cmsBody = asString(disclaimer?.body);
    const cmsPrimary = asString(disclaimer?.primary_cta_label);
    const cmsSecondary = asString(disclaimer?.secondary_cta_label);

    const source: 'cms' | 'fallback' =
      cmsTitle !== undefined ||
      cmsSubtitle !== undefined ||
      cmsBody !== undefined ||
      cmsPrimary !== undefined ||
      cmsSecondary !== undefined
        ? 'cms'
        : 'fallback';

    return {
      title: cmsTitle ?? fallback.title ?? '',
      subtitle: cmsSubtitle ?? fallback.subtitle ?? '',
      body: cmsBody ?? fallback.body ?? '',
      primaryCtaLabel: cmsPrimary ?? fallback.primaryCtaLabel ?? '',
      secondaryCtaLabel: cmsSecondary ?? fallback.secondaryCtaLabel ?? '',
      loading,
      source,
    };
  }, [data, loading]);
}

/**
 * Returns the resolved ARO ID.me step-up verification result screen content
 * (success or error), sourced from CMS with fallback to bundled defaults. All
 * fields degrade independently; `icon` falls back to the kind's hardcoded
 * default whenever the CMS value is missing or not a recognized icon name.
 */
export function useAroIdMeResultContent(
  kind: AroIdMeResultKind,
  options: UseAroIdMeResultContentOptions = {},
): AroIdMeResultContent {
  const { skip = false } = options;
  const { data, loading } = useGetUIContent<UIIdentityAroContent>(UI_CONTENT_IDS.IDENTITY, { skip });

  return useMemo<AroIdMeResultContent>(() => {
    const result = data?.getUIContentById?.aro_id_me?.[kind];
    const fallback = contentUnsafe.aroIdMeVerification?.[kind === 'error' ? 'failure' : 'success'] ?? {};
    const defaultIcon = ARO_RESULT_ICON_DEFAULTS[kind];

    const cmsTitle = asString(result?.title);
    const cmsBody = asString(result?.body);
    const cmsCtaLabel = asString(result?.cta_label);
    const cmsIcon = asIconName(result?.icon);

    const source: 'cms' | 'fallback' =
      cmsTitle !== undefined || cmsBody !== undefined || cmsCtaLabel !== undefined || cmsIcon !== undefined
        ? 'cms'
        : 'fallback';

    return {
      title: cmsTitle ?? fallback.title ?? '',
      body: cmsBody ?? fallback.description ?? '',
      ctaLabel: cmsCtaLabel ?? fallback.cta ?? '',
      icon: cmsIcon ?? defaultIcon,
      loading,
      source,
    };
  }, [data, loading, kind]);
}
